/**
 * Calm WebGL starfield for the astrology About section.
 * One canvas, paused off-screen, static when reduced motion is requested.
 */
(function () {
  'use strict';

  var VERT = [
    'attribute vec2 aSeed;',
    'uniform float uTime;',
    'uniform vec2 uResolution;',
    'uniform float uRotation;',
    'uniform float uTwinkle;',
    'uniform float uDrift;',
    'uniform float uPointScale;',
    'varying float vAlpha;',
    'void main() {',
    '  float spin = aSeed.y * 6.2831853 + uTime * uRotation;',
    '  float radius = mix(0.38, 1.22, pow(aSeed.x, 0.72));',
    '  vec2 p = vec2(cos(spin), sin(spin)) * radius;',
    '  p.x += sin(uTime * uDrift + aSeed.y * 14.0) * 0.012;',
    '  p.y += cos(uTime * uDrift * 0.8 + aSeed.x * 11.0) * 0.012;',
    '  float aspect = uResolution.x / max(uResolution.y, 1.0);',
    '  p.x /= aspect;',
    '  float tw = sin(uTime * (0.45 + aSeed.y * 0.7) + aSeed.x * 18.0);',
    '  float alpha = mix(0.55, 0.55 + tw * 0.45, uTwinkle);',
    '  alpha *= 0.28 + aSeed.y * 0.72;',
    '  vAlpha = alpha;',
    '  gl_Position = vec4(p, 0.0, 1.0);',
    '  gl_PointSize = (0.9 + aSeed.y * 1.8) * uPointScale;',
    '}'
  ].join('\n');

  var FRAG = [
    'precision mediump float;',
    'varying float vAlpha;',
    'void main() {',
    '  vec2 c = gl_PointCoord - vec2(0.5);',
    '  float d = length(c);',
    '  if (d > 0.5) discard;',
    '  float core = smoothstep(0.22, 0.0, d);',
    '  float halo = smoothstep(0.5, 0.12, d);',
    '  float alpha = (core * 0.9 + halo * 0.22) * vAlpha;',
    '  gl_FragColor = vec4(0.96, 0.95, 0.92, alpha);',
    '}'
  ].join('\n');

  function compile(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function densityFor(width) {
    if (width < 750) return 0.38;
    if (width < 990) return 0.62;
    return 0.85;
  }

  function pointScaleFor(width, dpr) {
    var scale = width < 750 ? 0.85 : 1;
    return scale * Math.min(dpr, 1.5);
  }

  function createField(gl, count) {
    var data = new Float32Array(count * 2);
    for (var i = 0; i < count; i++) {
      data[i * 2] = Math.random();
      data[i * 2 + 1] = Math.random();
    }
    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    return buffer;
  }

  function mount(canvas) {
    if (!canvas || canvas.dataset.galaxyReady === '1') return;
    var section = canvas.closest('.ar-astro');
    if (!section) return;

    var gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      powerPreference: 'low-power'
    });
    if (!gl) return;

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    var program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

    var aSeed = gl.getAttribLocation(program, 'aSeed');
    var uTime = gl.getUniformLocation(program, 'uTime');
    var uResolution = gl.getUniformLocation(program, 'uResolution');
    var uRotation = gl.getUniformLocation(program, 'uRotation');
    var uTwinkle = gl.getUniformLocation(program, 'uTwinkle');
    var uDrift = gl.getUniformLocation(program, 'uDrift');
    var uPointScale = gl.getUniformLocation(program, 'uPointScale');

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var count = 0;
    var buffer = null;
    var raf = 0;
    var running = false;
    var visible = false;
    var start = 0;

    function starCount() {
      var w = section.clientWidth || 1;
      var h = section.clientHeight || 1;
      var n = Math.round((w * h) / 1600 * densityFor(w));
      return Math.max(140, Math.min(n, 900));
    }

    function resize() {
      var w = section.clientWidth || 1;
      var h = section.clientHeight || 1;
      var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      var nextW = Math.max(1, Math.floor(w * dpr));
      var nextH = Math.max(1, Math.floor(h * dpr));
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);

      var nextCount = starCount();
      if (nextCount !== count) {
        if (buffer) gl.deleteBuffer(buffer);
        count = nextCount;
        buffer = createField(gl, count);
      }
    }

    function draw(now) {
      if (!start) start = now || 0;
      var t = reduced ? 0 : ((now || 0) - start) / 1000;
      var w = section.clientWidth || 1;

      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(aSeed);
      gl.vertexAttribPointer(aSeed, 2, gl.FLOAT, false, 0, 0);
      gl.uniform1f(uTime, t);
      gl.uniform2f(uResolution, canvas.width, canvas.height);
      gl.uniform1f(uRotation, reduced ? 0 : 0.03);
      gl.uniform1f(uTwinkle, reduced ? 0 : 0.2);
      gl.uniform1f(uDrift, reduced ? 0 : 0.22);
      gl.uniform1f(uPointScale, pointScaleFor(w, Math.min(window.devicePixelRatio || 1, 1.5)));
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.POINTS, 0, count);
    }

    function frame(now) {
      if (!running) return;
      draw(now);
      raf = window.requestAnimationFrame(frame);
    }

    function startLoop() {
      if (running || reduced || !visible) return;
      running = true;
      raf = window.requestAnimationFrame(frame);
    }

    function stopLoop() {
      running = false;
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    }

    var resizeTimer = 0;
    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        resize();
        if (!running) draw(performance.now());
      }, 80);
    }

    var io = new IntersectionObserver(function (entries) {
      visible = entries.some(function (entry) { return entry.isIntersecting; });
      if (visible) {
        resize();
        if (reduced) draw(0);
        else startLoop();
      } else {
        stopLoop();
      }
    }, { rootMargin: '80px 0px', threshold: 0 });

    window.addEventListener('resize', onResize, { passive: true });
    io.observe(section);
    canvas.dataset.galaxyReady = '1';

    canvas._galaxyDestroy = function () {
      stopLoop();
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      io.disconnect();
      if (buffer) gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      var lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      delete canvas._galaxyDestroy;
      delete canvas.dataset.galaxyReady;
    };
  }

  function boot(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-ar-astro-galaxy]');
    for (var i = 0; i < nodes.length; i++) mount(nodes[i]);
  }

  function unload(root) {
    if (!root || !root.querySelector) return;
    var canvas = root.querySelector('[data-ar-astro-galaxy]');
    if (canvas && canvas._galaxyDestroy) canvas._galaxyDestroy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unload(event.target); });
})();
